import React, { useCallback } from 'react';
import { View, Pressable, StyleSheet, Platform } from 'react-native';
import { useRouter } from 'expo-router';
import { useTheme } from '@/context/ThemeContext';
import { useModalStore } from '@/store';
import useResponsiveness from '@/hooks/useResponsiveness';
import Text from '@/components/Typography/Text';
import Icon from '@/components/Icon';
import { useChecklist, runChecklistAction, trackChecklistEvent } from '@/constants/checklist';

const isWeb = Platform.OS === 'web';

function ChecklistRow({ item, completed, onPress, colors, showCredits }) {
  const height = completed ? (isWeb ? 32 : 44) : 58;

  return (
    <Pressable
      onPress={onPress}
      accessibilityRole="button"
      accessibilityLabel={`${item.label}${completed ? ', completed' : ''}`}
      accessibilityHint={item.hint}
      style={({ hovered, pressed }) => [
        {
          minHeight: height,
          paddingVertical: 7,
          flexDirection: 'row',
          alignItems: 'center',
          paddingHorizontal: 10,
          borderRadius: isWeb ? 6 : 8,
          gap: isWeb ? 8 : 10,
        },
        isWeb && hovered && { backgroundColor: colors.solid_color_tertiary },
        !isWeb && pressed && { opacity: 0.7 },
      ]}
    >
      <View style={[
        styles.checkbox,
        completed
          ? { backgroundColor: colors.color_success, borderColor: colors.color_success }
          : { borderColor: colors.text_color_tertiary },
      ]}>
        {completed && <Icon name="check" size={8} color="#fff" />}
      </View>

      <View style={{ width: 20, height: 20, alignItems: 'center', justifyContent: 'center' }}>
        <Icon
          name={item.icon}
          size={isWeb ? 14 : 16}
          color={colors.text_color_tertiary}
          solid={completed}
        />
      </View>

      <View style={{ flex: 1, gap: 2 }}>
        <Text style={{
          fontSize: 14,
          fontWeight: '500',
          color: colors.text_color_primary,
          opacity: completed ? 0.5 : 1,
        }}>
          {item.label}
        </Text>
        {!completed && <Text style={{ fontSize: 11, lineHeight: 15, color: colors.text_color_tertiary }}>
          {item.hint}
        </Text>}
      </View>

      {showCredits && item.credits > 0 && (
        <View style={[styles.creditChip, {
          backgroundColor: `${colors.color_success}${completed ? '0F' : '1A'}`,
        }]}>
          <Text
            style={{
              fontSize: 11,
              fontWeight: '700',
              color: completed ? `${colors.color_success}66` : colors.color_success,
            }}
          >
            +{item.credits}
          </Text>
        </View>
      )}
    </Pressable>
  );
}

const MemoizedRow = React.memo(ChecklistRow);

// ─── Daily claim row (post-checklist) ────────────────────────

function DailyClaimRow({ navigation }) {
  const { colors } = useTheme();
  const { isXS } = useResponsiveness();
  const {
    dailyLoading, dailyAvailable, dailyClaiming, dailyJustClaimed, dailyCredits, claimDaily, shouldShowDaily,
  } = useChecklist();

  const handlePress = useCallback(async () => {
    if (!dailyAvailable || dailyClaiming) return;
    trackChecklistEvent('daily_claim_drawer_clicked');
    const ok = await claimDaily();
    if (ok && navigation) {
      setTimeout(() => navigation.closeDrawer(), 800);
    }
  }, [dailyAvailable, dailyClaiming, claimDaily, navigation]);

  if (!shouldShowDaily) return null;
  if (!dailyLoading && !dailyAvailable && !dailyJustClaimed) return null;

  const height = isWeb ? (isXS ? 40 : 32) : 44;
  const busy = dailyLoading || dailyClaiming;

  return (
    <Pressable
      onPress={handlePress}
      disabled={busy || dailyJustClaimed}
      style={({ hovered, pressed }) => [
        {
          height,
          flexDirection: 'row',
          alignItems: 'center',
          paddingHorizontal: 10,
          borderRadius: isWeb ? 6 : 8,
          gap: isWeb ? 8 : 10,
        },
        isWeb && hovered && !dailyJustClaimed && !busy && { backgroundColor: colors.solid_color_tertiary },
        !isWeb && pressed && !dailyJustClaimed && { opacity: 0.7 },
        busy && { opacity: 0.6 },
      ]}
    >
      <View style={{ width: 20, height: 20, alignItems: 'center', justifyContent: 'center' }}>
        <Icon
          name={dailyJustClaimed ? 'circle-check' : 'calendar-star'}
          size={isWeb ? 14 : 16}
          color={colors.color_success}
          solid
        />
      </View>

      <Text
        numberOfLines={1}
        style={{
          flex: 1,
          fontSize: isWeb ? 14.5 : 14,
          fontWeight: '425',
          color: dailyJustClaimed ? colors.color_success : colors.text_color_primary,
          lineHeight: isXS ? 22 : 20,
        }}
      >
        {dailyJustClaimed ? 'Claimed!' : 'Daily reward'}
      </Text>

      {!dailyJustClaimed && (
        <View style={[styles.earnBadge, { backgroundColor: `${colors.color_success}1F` }]}>
          <Text style={{ fontSize: 11, fontWeight: '700', color: colors.color_success }}>
            +{dailyCredits}
          </Text>
        </View>
      )}
    </Pressable>
  );
}

// ─── Checklist section (pre-completion) ──────────────────────

function ChecklistSection({ navigation }) {
  const { colors } = useTheme();
  const { isXS } = useResponsiveness();
  const router = useRouter();
  const { openModal } = useModalStore(state => state.actions);
  const {
    completionMap, completedCount, earnableCredits, sortedItems, total,
    dailyAvailable, dailyClaiming, dailyJustClaimed, dailyCredits, claimDaily,
    showCredits, collapsed, toggleCollapsed,
  } = useChecklist();

  const handleItemPress = useCallback((item) => {
    trackChecklistEvent('item_clicked', item.id);
    if (navigation) navigation.closeDrawer();
    if (item.action) {
      runChecklistAction(item.action, { router, openModal, isXS });
      return;
    }
    if (item.route) {
      router.push(item.route);
    }
  }, [router, navigation, openModal, isXS]);

  const handleEarnMore = useCallback(() => {
    trackChecklistEvent('earn_more_clicked');
    if (navigation) navigation.closeDrawer();
    if (isWeb) {
      openModal('SettingsModalWeb', { tab: 'earn' });
    } else {
      openModal('SettingsModalMobile', { initialScreen: 'earn' });
    }
  }, [openModal, navigation]);

  const handleDailyClaim = useCallback(async () => {
    if (!dailyAvailable || dailyClaiming) return;
    trackChecklistEvent('daily_claim_drawer_clicked');
    const ok = await claimDaily();
    if (ok && navigation) {
      setTimeout(() => navigation.closeDrawer(), 800);
    }
  }, [dailyAvailable, dailyClaiming, claimDaily, navigation]);

  const pct = Math.round((completedCount / total) * 100);
  const headerHeight = isWeb ? (isXS ? 40 : 32) : 44;
  const showDailyRow = showCredits && (dailyAvailable || dailyJustClaimed);

  return (
    <View>
      {/* Section header */}
      <Pressable
        accessibilityRole="button"
        accessibilityLabel="Get Started"
        accessibilityState={{ expanded: !collapsed }}
        aria-expanded={!collapsed}
        onPress={() => {
          toggleCollapsed();
          if (collapsed) trackChecklistEvent('expanded_drawer');
        }}
        style={({ hovered, pressed }) => [
          {
            height: headerHeight,
            flexDirection: 'row',
            alignItems: 'center',
            paddingLeft: isWeb ? 10 : 14,
            paddingRight: isWeb ? 10 : 10,
            borderRadius: isWeb ? 6 : 8,
            gap: isWeb ? 8 : 10,
          },
          isWeb && hovered && { backgroundColor: colors.solid_color_tertiary },
          !isWeb && pressed && { opacity: 0.7 },
        ]}
      >
        <View style={{ width: 18, height: 18, alignItems: 'center', justifyContent: 'center' }}>
          <Icon name={showCredits ? 'gift' : 'clipboard-check'} size={isWeb ? isXS ? 18 : 16 : 20} color={colors.color_success} solid />
        </View>

        <Text
          numberOfLines={1}
          style={{
            flex: 1,
            fontSize: isWeb ? 15 : 14,
            color: colors.text_color_primary,
            lineHeight: isXS ? 22 : 20,
            paddingLeft: isWeb ? 0 : 4,
          }}
        >
          Get Started
        </Text>

        <View style={styles.headerRight}>
          {showCredits && earnableCredits > 0 && (
            <View style={[styles.earnBadge, {
              backgroundColor: `${colors.color_success}1F`,
            }]}>
              <Text style={{ fontSize: 11, fontWeight: '700', color: colors.color_success }}>
                +{earnableCredits}
              </Text>
            </View>
          )}
          <Text size='xxs' weight='bold' color='tertiary'>
            {completedCount}/{total}
          </Text>
          <Icon
            name={collapsed ? 'chevron-right' : 'chevron-down'}
            size={isWeb ? 12 : 14}
            color={colors.text_color_tertiary}
          />
        </View>
      </Pressable>

      {/* Progress bar */}
      {!collapsed && (
        <View style={[styles.progressTrack, {
          backgroundColor: colors.solid_color_tertiary,
          marginHorizontal: 10,
        }]}>
          <View style={[styles.progressFill, {
            width: `${pct}%`,
            backgroundColor: colors.color_success,
          }]} />
        </View>
      )}

      {/* Expanded items */}
      {!collapsed && (
        <>
          {/* Daily claim row */}
          {showDailyRow && (
            <Pressable
              onPress={handleDailyClaim}
              disabled={dailyClaiming || dailyJustClaimed}
              style={({ hovered, pressed }) => [
                {
                  height: headerHeight,
                  flexDirection: 'row',
                  alignItems: 'center',
                  paddingHorizontal: 10,
                  borderRadius: isWeb ? 6 : 8,
                  gap: isWeb ? 8 : 10,
                },
                isWeb && hovered && !dailyJustClaimed && { backgroundColor: colors.solid_color_tertiary },
                !isWeb && pressed && !dailyJustClaimed && { opacity: 0.7 },
                dailyClaiming && { opacity: 0.6 },
              ]}
            >
              <View style={{ width: 20, height: 20, alignItems: 'center', justifyContent: 'center' }}>
                <Icon
                  name={dailyJustClaimed ? 'circle-check' : 'calendar-star'}
                  size={isWeb ? 14 : 16}
                  color={colors.color_success}
                  solid
                />
              </View>
              <Text
                numberOfLines={1}
                style={{
                  flex: 1,
                  fontSize: isWeb ? 14.5 : 14,
                  fontWeight: '425',
                  color: dailyJustClaimed ? colors.color_success : colors.text_color_primary,
                  lineHeight: isXS ? 22 : 20,
                }}
              >
                {dailyJustClaimed ? 'Claimed!' : 'Daily credits'}
              </Text>
              {!dailyJustClaimed && (
                <View style={[styles.earnBadge, { backgroundColor: `${colors.color_success}1F` }]}>
                  <Text style={{ fontSize: 11, fontWeight: '700', color: colors.color_success }}>
                    +{dailyCredits}
                  </Text>
                </View>
              )}
            </Pressable>
          )}

          {sortedItems.map((item) => (
            <MemoizedRow
              key={item.id}
              item={item}
              completed={completionMap[item.id]}
              onPress={() => handleItemPress(item)}
              colors={colors}
              showCredits={showCredits}
            />
          ))}

          {showCredits && (
            <Pressable
              onPress={handleEarnMore}
              style={({ hovered, pressed }) => [
                {
                  height: isWeb ? (isXS ? 36 : 28) : 40,
                  flexDirection: 'row',
                  alignItems: 'center',
                  paddingHorizontal: 8,
                  borderRadius: isWeb ? 6 : 8,
                  gap: isWeb ? 8 : 10,
                },
                isWeb && hovered && { backgroundColor: colors.solid_color_tertiary },
                !isWeb && pressed && { opacity: 0.7 },
              ]}
            >
              <View style={{ width: 18, height: 18, alignItems: 'center', justifyContent: 'center' }}>
                <Icon name="coins" size={18} color={colors.color_success} />
              </View>
              <Text
                numberOfLines={1}
                style={{
                  flex: 1,
                  fontSize: 15,
                  fontWeight: '400',
                  color: colors.color_success,
                }}
              >
                View all rewards
              </Text>
              <Icon name="arrow-up-right" size={14} color={colors.color_success} />
            </Pressable>
          )}
        </>
      )}
    </View>
  );
}

// ─── Router: picks the right mode ────────────────────────────

function NativeDrawerChecklist({ navigation }) {
  const { shouldShow, shouldShowDaily, isComplete } = useChecklist();

  if (shouldShow) return <ChecklistSection navigation={navigation} />;
  if (shouldShowDaily && isComplete) return <DailyClaimRow navigation={navigation} />;
  return null;
}

const styles = StyleSheet.create({
  checkbox: {
    width: 16,
    height: 16,
    borderRadius: 5,
    borderWidth: 1.5,
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
  creditChip: {
    paddingHorizontal: 5,
    paddingVertical: 1,
    borderRadius: 4,
  },
  headerRight: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
    flexShrink: 0,
  },
  earnBadge: {
    paddingHorizontal: 5,
    paddingVertical: 1,
    borderRadius: 4,
  },
  progressTrack: {
    height: 2,
    borderRadius: 1,
    overflow: 'hidden',
    marginBottom: 2,
  },
  progressFill: {
    height: '100%',
    borderRadius: 1,
  },
});

export default React.memo(NativeDrawerChecklist);
